import {
    Account,
    Application,
    Cleanroom,
    Collaborator,
    Enrichment,
    Relation,
    Table,
} from "../../../../srcV2/common/types/cleanroom";
import { applicationColumnTypeMap } from "../../../components/elements/Summary/constants";
import { extractColumnNameBasedOnFqtn } from "../../../../common/components/elements/Summary/Utils";
import { ApplicationId } from "../../../pages/CleanroomCreateInstall/cleanroomCreateInstall.types";
import {
    FQTCToFQTN,
    isSameRegionAndCloud,
    parseFQTNC,
} from "../../utils/Utils";
import {
    Application_AudienceOverlapAndSegmentation_Consumer,
    Application_AudienceOverlapAndSegmentation_Provider,
    Application_CustomAnalysis_Consumer,
    ApplicationHasColumnPreview,
    ApplicationTable,
    PrivacySettings,
} from "../../../pages/CleanroomCreateInstall/Step3/ApplicationInitialState";
import { TableType } from "../../../pages/AnalysisPage/AudienceOverlapAnalysis/audienceOverlap.types";
import {
    APPLICATION_IDS,
    CLEANROOM_SIDE,
    CLEANROOM_TYPES,
    disabledAnalysesForInternalTest,
} from "../../constants/constants";

class CleanroomManager {
    getProviderOrConsumerRun({ cleanroom }: { cleanroom: Cleanroom }) {
        if (
            cleanroom?.cleanroom_meta_type === CLEANROOM_TYPES.AMC ||
            cleanroom?.cleanroom_meta_type === CLEANROOM_TYPES.ADH
        ) {
            return CLEANROOM_SIDE.CONSUMER;
        }
        return cleanroom?.cleanroom_context as CLEANROOM_SIDE;
    }
    isCleanroomProvider({ cleanroom }: { cleanroom: Cleanroom }) {
        return (
            this.getProviderOrConsumerRun({ cleanroom }) ===
            CLEANROOM_SIDE.PROVIDER
        );
    }

    isInternalTestCleanroom({
        cleanroom,
        creatorAccount,
    }: {
        cleanroom: Cleanroom;
        creatorAccount?: Account;
    }) {
        const providerAccount = creatorAccount
            ? creatorAccount.identifier_lrc
            : cleanroom?.creator?.account?.identifier_lrc;
        const collaboratorAccounts = cleanroom?.collaborators?.map(
            (collab) => collab?.account?.identifier_lrc,
        );
        return collaboratorAccounts.includes(providerAccount);
    }

    getDisabledAnalysesForInternalTest({
        cleanroom,
    }: {
        cleanroom: Cleanroom;
    }) {
        const applications = cleanroom.creator.applications;
        return applications
            .map((app, index) => ({
                application_id: app.application_id,
                name: app.name,
                index,
            }))
            .filter((app) =>
                disabledAnalysesForInternalTest.includes(
                    app.application_id as APPLICATION_IDS,
                ),
            );
    }

    // Data Tables
    getCreatorDataTables({ cleanroom }: { cleanroom: Cleanroom }) {
        return cleanroom.creator?.tables || [];
    }

    getCreatorIdHub({ cleanroom }: { cleanroom: Cleanroom }) {
        return cleanroom.creator.enrichments || [];
    }

    getCreatorSecureJoin({ cleanroom }: { cleanroom: Cleanroom }) {
        return cleanroom.creator?.allowed_join_columns || [];
    }

    getSelectedAnalysisCreatorApplication({
        cleanroom,
        analysisType,
    }: {
        cleanroom: Cleanroom;
        analysisType: ApplicationId;
    }): Application {
        return cleanroom?.creator?.applications?.find(
            (app) => app.application_id === analysisType,
        );
    }

    getConsumerDataTables({
        cleanroom,
    }: {
        cleanroom: Cleanroom;
    }): Record<string, Table[]> {
        const consumerTableArr = cleanroom.collaborators?.reduce(
            (acc: Record<string, Table[]>, collab) => {
                // accLocator = corresponding tables array
                acc[collab.account.identifier_lrc] = collab?.tables;

                return acc;
            },
            {},
        );
        return consumerTableArr;
    }

    getConsumerTables({
        cleanroom,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        activeCollaboratorIdentifier?: string;
    }): Table[] {
        const collaborator = this.getCollaboratorForIdentifier({
            cleanroom,
            collaboratorIdentifier: activeCollaboratorIdentifier,
        });
        return collaborator?.tables || [];
    }

    getConsumerIdHub({
        cleanroom,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        activeCollaboratorIdentifier?: string;
    }): Enrichment[] {
        const collaborator = this.getCollaboratorForIdentifier({
            cleanroom,
            collaboratorIdentifier: activeCollaboratorIdentifier,
        });
        return collaborator?.enrichments || [];
    }

    getProviderApplication({
        cleanroom,
        applicationId,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
    }): Application {
        const application = cleanroom.creator?.applications?.find(
            (app) => app.application_id === applicationId,
        );
        return application;
    }

    getCollaboratorForIdentifier({
        cleanroom,
        collaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        collaboratorIdentifier: string;
    }): Collaborator | undefined {
        return cleanroom.collaborators?.find(
            (collaborator) =>
                collaborator?.account?.identifier_lrc ===
                collaboratorIdentifier,
        );
    }

    getConsumerApplicationsArray({
        cleanroom,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        activeCollaboratorIdentifier?: string;
    }): Application[] {
        const collaborator = this.getCollaboratorForIdentifier({
            cleanroom,
            collaboratorIdentifier: activeCollaboratorIdentifier,
        });
        const applications = collaborator?.applications;
        return applications || [];
    }

    getConsumerApplication({
        cleanroom,
        applicationId,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
        activeCollaboratorIdentifier: string;
    }): Application {
        const allApplications = this.getConsumerApplicationsArray({
            cleanroom,
            activeCollaboratorIdentifier,
        });
        const application = allApplications?.find(
            (app) => app.application_id === applicationId,
        ) as Application_AudienceOverlapAndSegmentation_Consumer;
        return application;
    }

    getColumnsInTableForApplications({
        applications,
        table,
    }: {
        applications: Application[];
        table: Table;
    }): string[] {
        const fqtn = table?.fqtn;

        const columnNamesForTable = new Set<string>();

        applications.forEach((application) => {
            Object.keys(application.configuration).forEach((columnType) => {
                if (applicationColumnTypeMap.hasOwnProperty(columnType)) {
                    application.configuration[columnType]?.forEach((fqtc) => {
                        const colName = extractColumnNameBasedOnFqtn({
                            fqtc,
                            fqtn,
                        });
                        if (colName) {
                            columnNamesForTable.add(colName);
                        }
                    });
                }
            });
        });

        return [...columnNamesForTable];
    }

    // Returns all columns in table for specified collaborator in cleanroom
    getConsumerColumnsInTable({
        cleanroom,
        activeCollaboratorIdentifier,
        table,
    }: {
        cleanroom: Cleanroom;
        activeCollaboratorIdentifier?: string;
        table: Table;
    }): string[] {
        const applications = this.getConsumerApplicationsArray({
            cleanroom,
            activeCollaboratorIdentifier,
        });

        const columns: Set<string> = new Set(
            this.getColumnsInTableForApplications({
                applications,
                table,
            }),
        );

        const fqtn = table.fqtn;

        const collaborator = this.getCollaboratorForIdentifier({
            cleanroom,
            collaboratorIdentifier: activeCollaboratorIdentifier,
        });

        // Adds applicable join policies for the table
        collaborator?.allowed_join_columns.forEach((fqtc) => {
            const colName = extractColumnNameBasedOnFqtn({
                fqtc,
                fqtn,
            });
            if (colName) {
                columns.add(colName);
            }
        });

        return [...columns];
    }

    getConsumerAnalysisFromApplicationsArray({
        applications,
        analysisId,
    }: {
        applications: Application[];
        analysisId: string;
    }): Application | undefined {
        return applications?.find(
            (application) => application.application_id === analysisId,
        );
    }

    getCrossTableRelation({
        cleanroom,
        currentUserIdentifier,
        activeCollaboratorIdentifier = "",
    }: {
        cleanroom: Cleanroom;
        currentUserIdentifier?: string;
        activeCollaboratorIdentifier: string;
    }): Relation[] {
        const isCleanroomProvider = this.isCleanroomProvider({
            cleanroom,
        });
        if (isCleanroomProvider) {
            const collaborator = this.getCollaboratorForIdentifier({
                cleanroom,
                collaboratorIdentifier: activeCollaboratorIdentifier,
            });
            return collaborator?.relations ?? [];
        }

        const collaborator = this.getCollaboratorForIdentifier({
            cleanroom,
            collaboratorIdentifier: currentUserIdentifier,
        });

        return collaborator?.relations ?? [];
    }

    getConsumerSecureJoinColumns({
        cleanroom,
        currentUserIdentifier,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        currentUserIdentifier?: string;
        activeCollaboratorIdentifier: string;
    }): string[] {
        const crossRelations = this.getCrossTableRelation({
            cleanroom,
            currentUserIdentifier,
            activeCollaboratorIdentifier,
        }); // Collaborator:Provider

        const secureJoins = crossRelations?.reduce(
            (acc: string[], cr: Relation) => {
                acc.push(cr.source_column);
                return acc;
            },
            [],
        );

        return secureJoins;
    }

    getSelectedAnalysisCollaboratorApplication({
        cleanroom,
        analysisType,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        analysisType: ApplicationId;
        activeCollaboratorIdentifier: string;
    }): Application {
        const collaborator =
            cleanroom?.collaborators.find(
                (collab) =>
                    collab.account.identifier_lrc ===
                    activeCollaboratorIdentifier,
            ) || cleanroom?.collaborators[0];
        return collaborator?.applications?.find(
            (app) => app.application_id === analysisType,
        );
    }

    /**
     * Gets the list of tables available for the "My Table" selection in an analysis
     *
     * For consumer runs (isOwner=false):
     * - Finds the collaborator matching the currentUserIdentifier
     * - Gets that collaborator's applications
     * - Finds the application matching the analysisId
     * - Returns the tables from that application
     *
     * For provider runs (isOwner=true):
     * - Gets the creator's applications
     * - Finds the application matching the analysisId
     * - Returns the tables from that application
     */
    getMyTableListFromActiveAnalysis({
        cleanroom,
        analysisId,
        currentUserIdentifier,
    }: {
        cleanroom: Cleanroom;
        analysisId: ApplicationId;
        currentUserIdentifier: string;
    }): ApplicationTable[] {
        const isCleanroomProvider = this.isCleanroomProvider({
            cleanroom,
        });

        const applications = isCleanroomProvider
            ? cleanroom?.creator?.applications
            : cleanroom?.collaborators?.find(
                  (collaborator) =>
                      collaborator?.account?.identifier_lrc ===
                      currentUserIdentifier,
              )?.applications;

        const application = applications?.find(
            (application) => application.application_id === analysisId,
        );

        return application?.tables ?? [];
    }

    /**
     * Gets the list of tables available for the "Collaborator Table" selection in an analysis
     *
     * For consumer runs (isOwner=false):
     * - Gets the creator's applications
     * - Finds the application matching the analysisId
     * - Returns the tables from that application
     *
     * For provider runs (isOwner=true):
     * - Finds the collaborator matching the activeCollaboratorIdentifier
     * - Gets that collaborator's applications
     * - Finds the application matching the analysisId
     * - Returns the tables from that application
     */
    getCollaboratorTableListFromActiveAnalysis({
        cleanroom,
        analysisId,
        currentUserIdentifier,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        analysisId: ApplicationId;
        currentUserIdentifier: string;
        activeCollaboratorIdentifier: string;
    }): ApplicationTable[] {
        const isCleanroomProvider = this.isCleanroomProvider({
            cleanroom,
        });

        const applications = !isCleanroomProvider
            ? cleanroom?.creator?.applications
            : cleanroom?.collaborators?.find(
                  (collab) =>
                      collab?.account?.identifier_lrc ===
                      activeCollaboratorIdentifier,
              )?.applications;

        const application = applications?.find(
            (application) => application.application_id === analysisId,
        );

        return application?.tables ?? [];
    }

    /*
    Output: {DB.SCHEMA.TABLE: ['DB.SCHEMA.TABLE']}
    */

    getCrossRelationOneToManyMaping({
        cleanroom,
        currentUserIdentifier,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        currentUserIdentifier: string;
        activeCollaboratorIdentifier: string;
    }): Record<string, string[]> {
        const isCleanroomProvider = this.isCleanroomProvider({
            cleanroom,
        });
        const crossRelations = this.getCrossTableRelation({
            cleanroom,
            currentUserIdentifier,
            activeCollaboratorIdentifier,
        });
        // {source_column: 'DB.SCHEMA.TABLE', destination_column: 'DB.SCHEMA.TABLE'}
        // source is the consumer, destination is the provider
        const relationMapping: Record<string, Set<string>> = {};
        if (isCleanroomProvider) {
            // Provider:Consumer
            crossRelations?.forEach((relation) => {
                const source_column = relation?.source_column;
                const destination_column = relation?.destination_column;
                const destinationFQTN = FQTCToFQTN(destination_column);
                const sourceFQTN = FQTCToFQTN(source_column);
                if (relationMapping[destinationFQTN]) {
                    relationMapping[destinationFQTN].add(sourceFQTN);
                } else {
                    relationMapping[destinationFQTN] = new Set([sourceFQTN]);
                }
            });
        } else {
            // Consumer:Provider
            crossRelations?.forEach((relation) => {
                const source_column = relation?.source_column;
                const destination_column = relation?.destination_column;
                const sourceFQTN = FQTCToFQTN(source_column);
                const destinationFQTN = FQTCToFQTN(destination_column);
                if (relationMapping[sourceFQTN]) {
                    relationMapping[sourceFQTN].add(destinationFQTN);
                } else {
                    relationMapping[sourceFQTN] = new Set([destinationFQTN]);
                }
            });
        }
        // Convert sets to arrays
        const result: Record<string, string[]> = {};
        for (const key in relationMapping) {
            result[key] = Array.from(relationMapping[key]);
        }
        return result;
    }

    getApplicationFromCleanroom({
        cleanroom,
        applicationId,
        type,
        currentUserIdentifier,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
        type: TableType;
        currentUserIdentifier: string;
        activeCollaboratorIdentifier: string;
    }): Application {
        const isCleanroomProvider = this.isCleanroomProvider({
            cleanroom,
        });

        const isCreatorParty =
            (isCleanroomProvider && type === TableType.MyTable) ||
            (!isCleanroomProvider && type === TableType.CollaboratorTable);

        const party = isCreatorParty
            ? cleanroom?.creator
            : cleanroom?.collaborators?.find(
                  (collaborator) =>
                      collaborator.account.identifier_lrc ===
                      (isCleanroomProvider
                          ? activeCollaboratorIdentifier
                          : currentUserIdentifier),
              );

        return (
            party?.applications?.find(
                (application) => application.application_id === applicationId,
            ) || ({} as Application)
        );
    }

    getIsColumnPreviewEnabled({
        cleanroom,
        applicationId,
        currentUserIdentifier,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
        currentUserIdentifier: string;
        activeCollaboratorIdentifier: string;
    }): boolean | undefined {
        let application: ApplicationHasColumnPreview;
        if (!this.isCleanroomProvider({ cleanroom })) {
            application = cleanroom?.creator?.applications?.find(
                (application) => applicationId === application.application_id,
            ) as ApplicationHasColumnPreview;
        } else {
            const currentApplications =
                cleanroom?.collaborators?.find(
                    (item) =>
                        item?.account?.identifier_lrc ===
                        activeCollaboratorIdentifier,
                )?.applications || [];
            application = currentApplications?.find(
                (application) => applicationId === application.application_id,
            ) as ApplicationHasColumnPreview;
        }
        if (
            application &&
            application.configuration &&
            "column_data_preview" in application.configuration
        ) {
            return application.configuration.column_data_preview;
        }
        return false;
    }

    getPrivacySettings({
        cleanroom,
        applicationId,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
    }): PrivacySettings {
        return (
            cleanroom?.creator?.applications?.find(
                (application) => applicationId === application.application_id,
            ) as Application_AudienceOverlapAndSegmentation_Provider
        )?.privacy_settings;
    }

    getProviderActivationSettings({
        cleanroom,
        applicationId,
    }: {
        cleanroom: Cleanroom;
        applicationId: ApplicationId;
    }) {
        const application = this.getProviderApplication({
            cleanroom,
            applicationId,
        }) as Application_AudienceOverlapAndSegmentation_Provider;

        return {
            is_non_overlap_activation:
                application?.configuration?.is_non_overlap_activation || false,
        };
    }

    getConsumerActivationSettings({
        cleanroom,
        applicationId,
        activeCollaboratorIdentifier,
    }) {
        const application = this.getConsumerApplication({
            cleanroom,
            applicationId,
            activeCollaboratorIdentifier,
        }) as
            | Application_AudienceOverlapAndSegmentation_Consumer
            | Application_CustomAnalysis_Consumer;

        return {
            provider_activation:
                application?.configuration?.provider_activation || false,
        };
    }

    fqtcTofqtn(fqtc) {
        var parts = parseFQTNC(fqtc) || [];
        // var parts = fqtc.split(".") || [];
        // Remove the last element from the array
        parts.pop();
        // Join the array back into a string using '.'
        var result = parts.join(".");
        return result;
    }

    getCleanroomFeatures({ cleanroom }: { cleanroom: Cleanroom }): {
        isLafCleanroom: boolean;
    } {
        const creatorIdentifierLrc =
            cleanroom?.creator?.account?.identifier_lrc;
        const isAllCreatorAndCollaboratorsOnSameRegionAndCloud =
            cleanroom?.collaborators?.every((collab) =>
                isSameRegionAndCloud({
                    identifier: creatorIdentifierLrc,
                    accountIdentifier: collab?.account?.identifier_lrc,
                }),
            );

        return {
            isLafCleanroom: !isAllCreatorAndCollaboratorsOnSameRegionAndCloud,
        };
    }

    getIsScheduledRunEnabledOnCollaborator({
        cleanroom,
        activeCollaboratorIdentifier,
    }: {
        cleanroom: Cleanroom;
        activeCollaboratorIdentifier: string;
    }): boolean {
        return cleanroom?.collaborators?.find(
            (collab) =>
                collab?.account?.identifier_lrc ===
                activeCollaboratorIdentifier,
        )?.is_schedule_run_enabled;
    }
}

const CleanroomManagerV2 = new CleanroomManager();

export default CleanroomManagerV2;
